import { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import InsightQuickFilters from '../components/intelligence/InsightQuickFilters.jsx';
import InsightsLoadError from '../components/intelligence/InsightsLoadError.jsx';
import InsightsNotice from '../components/intelligence/InsightsNotice.jsx';
import InsightsResults from '../components/intelligence/InsightsResults.jsx';
import {
  DEFAULT_INSIGHT_QUICK_FILTER,
  INSIGHT_FILTERS,
  INSIGHT_SEARCH_FIELDS,
  INSIGHT_SORT_DIRECTION,
  INSIGHT_SORT_FIELD,
  INSIGHT_SORTS,
} from '../constants/intelligence.js';
import { ITEM_KINDS } from '../constants/itemSlideOut.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useInsights } from '../hooks/useInsights.js';
import { useItemParam } from '../hooks/useItemParam.js';
import {
  INSIGHTS_LIST_STATE,
  insightsListState,
  isInsightsListSettled,
  showsInsightQuickFilters,
} from '../utils/insightsListState.js';
import { formatTraitDate } from '../utils/traitDates.js';

import { PauseCircle, Sprout } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({
  defaultSortField: INSIGHT_SORT_FIELD,
  defaultSortDirection: INSIGHT_SORT_DIRECTION,
  searchFields: INSIGHT_SEARCH_FIELDS,
});

/**
 * The Insights subtab of Intelligence (CT-5716): patterns in the person's
 * emails, calls and meetings, and how often they hold — a two-column card grid
 * (one column below `md`).
 *
 * Same toolbar as Traits (`DataListFilterBar` + staged `DataListFilterSlideout`
 * + `useDataListFilters`) and the same chips pattern; the server narrows.
 * "Show emerging (N)" reveals the insights below the threshold; it is local view
 * state and closes again with "Clear all" (open question 8).
 *
 * What the grid shows is one value from `insightsListState` (no pattern yet: a
 * notice, and every insight shown as emerging; paused: a notice, and the cards
 * read as paused; no results; error), painted by `InsightsResults`.
 */
function InsightsPage() {
  const { t, i18n } = useTranslation('unified-profiles');
  const { entityType, entityId, entity, subtabSwitch } = useOutletContext();
  const name = entity?.title ?? t('profile.untitled');

  const [quickFilter, setQuickFilter] = useState(DEFAULT_INSIGHT_QUICK_FILTER);
  // `null` until the user toggles: then the default follows the state (open while no pattern holds).
  const [emergingToggled, setEmergingToggled] = useState(null);
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll } = filtersState;

  const { items, emerging, interactionsSoFar, paused, total, loading, error, accessDenied, refetch } = useInsights({
    entityType,
    entityId,
    quickFilter,
    orderBy,
    orderDirection,
    filters,
    search,
  });

  const { item, openItem } = useItemParam();
  const openInsightId = item?.kind === ITEM_KINDS.INSIGHT ? item.id : null;
  const handleOpen = useCallback((insight) => openItem(ITEM_KINDS.INSIGHT, insight.id), [openItem]);

  const filterDefs = useMemo(
    () =>
      INSIGHT_FILTERS.map(({ id, options }) => ({
        id,
        label: t(`intelligence.insights.filters.${id}.label`),
        options: options.map((value) => ({ value, label: t(`intelligence.insights.trend.${value}`) })),
      })),
    [t],
  );
  const sortDefs = useMemo(
    () => INSIGHT_SORTS.map((value) => ({ value, label: t(`intelligence.insights.sort.${value}`) })),
    [t],
  );


  const clearEverything = useCallback(() => {
    clearAll();
    setQuickFilter(DEFAULT_INSIGHT_QUICK_FILTER);
    setEmergingToggled(null);
  }, [clearAll]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);

  const listState = insightsListState({
    loading,
    accessDenied,
    error,
    itemCount: items.length,
    emergingCount: emerging.count,
    interactionsSoFar,
    narrowed: hasActiveQuery || quickFilter !== DEFAULT_INSIGHT_QUICK_FILTER,
  });
  const settled = isInsightsListSettled(listState);
  const showEmerging = emergingToggled ?? listState === INSIGHTS_LIST_STATE.NO_PATTERN;
  const toggleEmerging = useCallback(() => setEmergingToggled(!showEmerging), [showEmerging]);

  return (
    <div data-testid="insights-page" data-quick-filter={quickFilter} className="flex min-w-0 flex-1 flex-col gap-4 pb-6">
      <div className="flex shrink-0 items-start gap-3">
        {subtabSwitch}
        <div className="min-w-0 flex-1">
          <DataListFilterBar
            filtersState={filtersState}
            filterDefs={filterDefs}
            sortDefs={sortDefs}
            searchPlaceholder={t('intelligence.insights.searchPlaceholder')}
            onAllFiltersClick={openAllFilters}
          />
        </div>
      </div>

      <p className="text-sm text-content-secondary">{t('intelligence.insights.description', { name })}</p>

      {listState === INSIGHTS_LIST_STATE.NO_PATTERN ? (
        <InsightsNotice testId="insights-no-pattern" icon={Sprout}>
          {t('intelligence.insights.noPattern', { count: interactionsSoFar })}
        </InsightsNotice>
      ) : null}
      {error && !accessDenied ? <InsightsLoadError onRetry={refetch} /> : null}
      {settled && paused ? (
        <InsightsNotice testId="insights-paused" icon={PauseCircle}>
          {t(`intelligence.insights.paused.${paused.source}`, {
            date: formatTraitDate(paused.since, i18n?.language),
          })}
        </InsightsNotice>
      ) : null}

      {showsInsightQuickFilters(listState) ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <InsightQuickFilters value={quickFilter} onChange={setQuickFilter} />
          {settled ? (
            <span data-testid="insights-count" className="text-sm text-content-muted">
              {t('intelligence.insights.count', { count: total })}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <InsightsResults
          state={listState}
          items={items}
          emerging={emerging}
          showEmerging={showEmerging}
          onToggleEmerging={toggleEmerging}
          openInsightId={openInsightId}
          onOpenInsight={handleOpen}
          onClearFilters={clearEverything}
        />
      </div>

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
    </div>
  );
}

export default InsightsPage;
